import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ArrowUp, Copy, Loader2, X } from 'lucide-react';
import { AuroraLogo, OFFICE_APPS } from '../../office/icons';
import { markdownToHtml } from '../../office/markdown';
import '../../office/office.css';
import { errorAurora, pedirAurora } from './aurora';

const SUGERENCIAS = [
  '¿Qué preguntas le faltan a este formulario?',
  '¿Alguna pregunta se puede entender mal?',
  '¿Cómo lo hago más corto sin perder lo importante?',
  '¿Qué debería ser obligatorio?',
];

/**
 * Panel lateral de Aurora IA en el editor de formularios: conversación sobre el formulario abierto.
 * Es el mismo panel que en Orvian Docs/Sheets (mismas clases `zo-ai-*`), con lo que cambia aquí: el
 * contexto es el formulario —preguntas, tipos y ajustes— y para CAMBIARLO está la barra de abajo.
 */
export default function PanelAurora({ formId, titulo, nombreUsuario, onCerrar }) {
  const [mensajes, setMensajes] = useState([]);
  const [texto, setTexto] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const listaRef = useRef(null);
  const entradaRef = useRef(null);

  useEffect(() => {
    listaRef.current?.scrollTo({ top: listaRef.current.scrollHeight, behavior: 'smooth' });
  }, [mensajes, ocupado]);

  useEffect(() => {
    entradaRef.current?.focus();
  }, []);

  const enviar = async (valor) => {
    const pregunta = (valor ?? texto).trim();
    if (!pregunta || ocupado) return;
    const siguiente = [...mensajes, { role: 'user', content: pregunta }];
    setMensajes(siguiente);
    setTexto('');
    setOcupado(true);
    try {
      const res = await pedirAurora(formId, {
        accion: 'chat',
        mensajes: siguiente.filter((m) => !m.error).map(({ role, content }) => ({ role, content })),
      });
      setMensajes([...siguiente, { role: 'assistant', content: res.texto }]);
    } catch (err) {
      setMensajes([...siguiente, { role: 'assistant', content: errorAurora(err), error: true }]);
    } finally {
      setOcupado(false);
    }
  };

  const copiar = async (valor) => {
    try {
      await navigator.clipboard.writeText(valor);
      toast.success('Copiado');
    } catch {
      toast.error('No se pudo copiar');
    }
  };

  const nombre = (nombreUsuario || '').split(' ')[0];

  return (
    <aside className="zo-ai-panel fe-panel-aurora" data-testid="fe-panel-aurora">
      <div className="zo-ai-panel-head">
        <AuroraLogo size={22} />
        <div className="zo-ai-panel-title">
          <strong>Aurora IA</strong>
          <span>En {OFFICE_APPS.form.name}</span>
        </div>
        <button type="button" className="zo-icon-btn" onClick={onCerrar} aria-label="Cerrar panel"><X className="h-4 w-4" /></button>
      </div>

      <div className="zo-ai-panel-list" ref={listaRef}>
        {mensajes.length === 0 && (
          <div className="zo-ai-hello">
            <div className="zo-ai-hello-title">
              Hola{nombre ? `, ${nombre}` : ''}.
              <br />
              ¿Qué hacemos con este formulario?
            </div>
            <p>
              Aurora IA lee {titulo ? `«${titulo}»` : 'tu formulario'} —sus preguntas, tipos y ajustes— para
              aconsejarte. Para añadir o cambiar preguntas, usa la barra de abajo.
            </p>
            <div className="zo-ai-suggestions">
              {SUGERENCIAS.map((s) => (
                <button key={s} type="button" onClick={() => enviar(s)}>{s}</button>
              ))}
            </div>
          </div>
        )}
        {mensajes.map((m, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={i} className={`zo-msg zo-msg-${m.role} ${m.error ? 'is-error' : ''}`}>
            {m.role === 'user' ? (
              <div className="zo-msg-bubble">{m.content}</div>
            ) : (
              <>
                {/* markdownToHtml escapa todo el texto antes de agregar etiquetas */}
                <div className="zo-md" dangerouslySetInnerHTML={{ __html: markdownToHtml(m.content) }} />
                {!m.error && (
                  <div className="zo-msg-actions">
                    <button type="button" onClick={() => copiar(m.content)}><Copy className="h-3.5 w-3.5" /> Copiar</button>
                  </div>
                )}
              </>
            )}
          </div>
        ))}
        {ocupado && (
          <div className="zo-msg zo-msg-assistant">
            <div className="zo-typing"><span /><span /><span /></div>
          </div>
        )}
      </div>

      <form
        className="zo-ai-panel-input"
        onSubmit={(e) => {
          e.preventDefault();
          enviar();
        }}
      >
        <textarea
          ref={entradaRef}
          rows={2}
          value={texto}
          placeholder="Pregunta sobre este formulario…"
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              enviar();
            }
          }}
        />
        <button type="submit" className="zo-send" disabled={!texto.trim() || ocupado} aria-label="Enviar">
          {ocupado ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-4 w-4" />}
        </button>
      </form>
      <p className="zo-ai-disclaimer">Aurora IA puede equivocarse. Revisa la información importante.</p>
    </aside>
  );
}
